// Truvolv step-scoped events (Form Step X of Y Started/Completed/Viewed), driven centrally from
// route transitions + first focus so individual pages don't each need instrumenting. Mirrors
// heavyset-form-ui/app/lib/truvolv-steps.js — keep the two in sync.
//
// Step model: each enabled step maps to a 1-based index and a TOTAL that is stable for a given
// form config. The multi-page lead entry (email + person-info, or the consolidated/contact-first/
// zip-first variants) is intentionally collapsed into a single `contact` step so the funnel is
// comparable across form modes.
//
//   step-viewed    — on entry to each step's route (every visit)
//   step-started   — first interaction while on that step (once per step), backfilled on exit for
//                    steps that auto-advance without any interaction at all
//   step-completed — when the user advances to a higher-indexed step or a success terminal;
//                    forward navigation only happens on a successful submit.
//
// The backend re-validates and dedups, so re-fires here are harmless.

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { jwtToken } from '../context';
import { TruvolvEnabled } from '../context/style-data';
import { submitTruvolvEvent } from '../services/truvolv-event';
import { onFirstInteraction } from '../util/first-interaction';

const STEP_CATALOG = [
  { key: 'contact', enabled: () => true },
  { key: 'product', enabled: (d) => Array.isArray(d?.text?.products) && d.text.products.length > 0 },
  { key: 'hearAbout', enabled: (d) => Array.isArray(d?.text?.hearAbout) && d.text.hearAbout.length > 0 },
  { key: 'staff', enabled: (d) => !!d?.showStaff },
  { key: 'homeYear', enabled: (d) => !!d?.showHomeYear },
  { key: 'details', enabled: (d) => !!d?.showDetails },
  { key: 'schedule', enabled: (d) => !d?.leadOnlyForm },
  // Post-appointment steps (only exist once there's an appointment). Post-appt details
  // (showDetailsEnd) takes routing precedence over the notes page — when it's on, the schedule
  // page routes to it and the notes page is skipped — so notes is a step only when it's off.
  { key: 'postApptDetails', enabled: (d) => !!d?.showDetailsEnd && !d?.leadOnlyForm },
  { key: 'notes', enabled: (d) => !!d?.showNotesPage && !d?.showDetailsEnd && !d?.leadOnlyForm }
];

// The appointment confirmation/status page is not a funnel step — displaying it fires the
// dedicated 'Form Confirmation Message' event instead (see useTruvolvSteps).
const CONFIRMATION_ROUTE = '/appointment-status';

const ROUTE_STEP_KEYS = {
  '/': 'contact',
  '/consolidated-form': 'contact',
  '/basic-horizontal-form': 'contact',
  '/contact-first': 'contact',
  '/person-info': 'contact',
  '/zip-first': 'contact',
  '/contact': 'contact',
  '/select-product': 'product',
  '/select-hear-about': 'hearAbout',
  '/select-staff': 'staff',
  '/set-home-year': 'homeYear',
  '/enter-details': 'details',
  '/schedule': 'schedule',
  '/zip-first-schedule': 'schedule',
  '/enter-post-appt-details': 'postApptDetails',
  '/appointment-notes': 'notes'
};

const SUCCESS_TERMINALS = new Set(['/appointment-status', '/form-complete']);

// Steps that auto-advance (a single product or hearAbout option, a slot pre-selected on the host
// page) render only a spinner and submit themselves, so no interaction ever happens and
// step-started could never fire — leaving Completed above Started in Truvolv's funnel. Backfill
// step-started for any step leaving without one, then always chain step-completed off that
// request so the pair can't be recorded out of order — including when the user completes an
// interactive step before its own step-started has landed. Already-settled is the common case,
// where the chain costs a microtask.
function completeStep(step, startedRef) {
  const payload = { step: step.step, totalSteps: step.totalSteps };
  const complete = () => submitTruvolvEvent('step-completed', payload);
  if (!startedRef.current) {
    startedRef.current = Promise.resolve(submitTruvolvEvent('step-started', payload));
  }
  startedRef.current.then(complete, complete);
}

function computeStepMap(styleData) {
  const enabled = STEP_CATALOG.filter((s) => s.enabled(styleData));
  const totalSteps = enabled.length;
  const map = {};
  enabled.forEach((s, i) => {
    map[s.key] = { step: i + 1, totalSteps };
  });
  return map;
}

export function useTruvolvSteps() {
  const location = useLocation();
  // styleData lives on the mutable jwtToken singleton and is only populated after auth resolves.
  // Flip a state flag on auth-ready so the effect re-runs and can fire for the landing route.
  const [ready, setReady] = useState(!!jwtToken?.token);
  const currentStepRef = useRef(null);
  // The current step's in-flight (or settled) step-started request, null before it fires. Holding
  // the request rather than a boolean is what lets step-completed chain off it.
  const startedRef = useRef(null);

  useEffect(() => {
    const onReady = () => setReady(true);
    window.addEventListener('hvs:auth-ready', onReady);
    return () => window.removeEventListener('hvs:auth-ready', onReady);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined' || !ready || !TruvolvEnabled()) return;

    const styleData = jwtToken?.data?.styleData || {};
    const map = computeStepMap(styleData);
    const stepKey = ROUTE_STEP_KEYS[location.pathname];
    const info = stepKey ? map[stepKey] : null;
    const prev = currentStepRef.current;

    if (prev && (SUCCESS_TERMINALS.has(location.pathname) || (info && info.step > prev.step))) {
      completeStep(prev, startedRef);
    }

    // Confirmation message shown to the user — the dedicated Form Confirmation Message event
    // (deduped once per session on the backend).
    if (location.pathname === CONFIRMATION_ROUTE) {
      submitTruvolvEvent('form-confirmation');
    }

    // A success terminal ends the funnel. Every other uncounted route (/waiting-for-crm,
    // /follow-up, /already-scheduled, /unsupported-postal-code, /contact-error) is a pass-through
    // — keep the current step so advancing out of it still completes the step the user actually
    // finished instead of silently dropping its step-completed.
    if (!info) {
      if (SUCCESS_TERMINALS.has(location.pathname)) {
        currentStepRef.current = null;
      }
      return;
    }

    // Re-arm step-started only when the logical step actually changes. A single step spans several
    // routes (e.g. contact: / -> /person-info), and those transitions must not fire a second
    // step-started for the same step (it's once-per-step).
    if (!prev || prev.key !== stepKey) {
      startedRef.current = null;
    }
    currentStepRef.current = { key: stepKey, step: info.step, totalSteps: info.totalSteps };
    submitTruvolvEvent('step-viewed', { step: info.step, totalSteps: info.totalSteps });

    // Guarded because one step spans several routes (contact: / -> /person-info) and each route
    // re-arms a fresh listener — step-started stays once per step.
    return onFirstInteraction(() => {
      const cur = currentStepRef.current;
      if (startedRef.current || !cur) return;
      startedRef.current = Promise.resolve(
        submitTruvolvEvent('step-started', { step: cur.step, totalSteps: cur.totalSteps })
      );
    });
  }, [location.pathname, ready]);
}
